import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  BarController,
} from "chart.js";
import nlp from "compromise";
import { filterReviewsByPlatform } from "../../utility/reviewByPlatforms";
import "./analysisGraph.css";

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const AnalysisGraph = ({ reviews, chartId = "analysisGraphChart" }) => {
  const [feedbackIssues, setFeedbackIssues] = useState({});
  const [selectedPlatform, setSelectedPlatform] = useState("All"); // NEW state
  const chartRef = useRef(null);
  const [selectedProductCode, setSelectedProductCode] = useState("All");

  const complaintCategories = useMemo(
    () => ({
      "Bad Organization": [
        "disorganized",
        "confusing",
        "chaotic",
        "unplanned",
        "miscommunication",
        "unclear instructions",
      ],
      "Guide Issues": [
        "unprofessional",
        "late",
        "arrogant",
        "soft spoken",
        "monotonous",
        "uninspiring",
        "poor communication",
        "not engaging",
        "low energy",
        "inaccurate information",
        "biased",
        "political opinions",
      ],
      "Customer Service": [
        "unhelpful",
        "no response",
        "ignored",
        "bad support",
        "no refund",
        "inflexible",
        "poor management",
        "calling",
        "phone",
      ],
      "Pricing Issues": [
        "too expensive",
        "overpriced",
        "not worth it",
        "expensive for value",
        "misleading price",
        "waste of money",
      ],
      "Timing Issues": [
        "started late",
        "ended early",
        "missed the tour",
        "long wait",
        "shortened",
        "delayed",
        "time wasted",
      ],
      "Guide Not Found": [
        "couldn't find guide",
        "lost",
        "missing",
        "no one showed up",
        "no contact",
        "wrong meeting point",
        "couldn't find our tour guide",
        "meeting point",
      ],
      Overcrowding: [
        "crowded",
        "too many people",
        "packed",
        "overwhelmed",
        "small rooms",
        "cramped",
      ],
      "Headset and Audio Issues": [
        "couldn't hear",
        "not audible",
        "no headsets",
        "no microphone",
        "soft spoken",
        "hard to hear",
        "poor audio",
      ],
      "Misleading Description": [
        "misleading",
        "not as described",
        "false advertisement",
        "description inaccurate",
        "wrong details",
        "misleading information",
        "Buckingham",
      ],
    }),
    []
  );

  const contextRules = {
    "Customer Service": [
      "customer service",
      "support",
      "operator",
      "company",
      "help desk",
      "calling",
      "call",
      "phone",
    ],
    "Guide Issues": ["guide", "tour leader", "our guide"],
  };

  const productCodes = useMemo(() => {
    const codes = reviews
      .map((r) => (r[6] ? String(r[6]).trim() : null))
      .filter((code) => code);
    return Array.from(new Set(codes));
  }, [reviews]);

  useEffect(() => {
    const analyzeNegativeReviews = () => {
      const issueMap = {
        "Bad Organization": 0,
        "Guide Issues": 0,
        "Customer Service": 0,
        "Pricing Issues": 0,
        "Timing Issues": 0,
        "Guide Not Found": 0,
        Overcrowding: 0,
        "Headset and Audio Issues": 0,
        "Misleading Description": 0,
      };

      // Filter reviews based on dropdown
      let filteredReviews = reviews;
      const {
        gygReviews,
        headoutReviews,
        tiquetsReviews,
        viatorReviews,
        civitatisReviews,
      } = filterReviewsByPlatform(reviews);

      if (selectedPlatform === "GYG") filteredReviews = gygReviews;
      else if (selectedPlatform === "HEA") filteredReviews = headoutReviews;
      else if (selectedPlatform === "Tiquets") filteredReviews = tiquetsReviews;
      else if (selectedPlatform === "Viator") filteredReviews = viatorReviews;
      else if (selectedPlatform === "Civitatis")
        filteredReviews = civitatisReviews;

      if (selectedProductCode !== "All") {
        filteredReviews = filteredReviews.filter(
          (r) => r[6] && String(r[6]).trim() === selectedProductCode
        );
      }

      filteredReviews.forEach((review) => {
        const rating = review[4];
        const comment = review[5];

        if (rating && comment && rating <= 3) {
          const doc = nlp(comment.toLowerCase());

          Object.entries(complaintCategories).forEach(
            ([category, keywords]) => {
              keywords.forEach((keyword) => {
                if (doc.has(keyword)) {
                  if (keyword === "rude") {
                    const text = doc.text();
                    if (
                      contextRules["Customer Service"].some((phrase) =>
                        text.includes(phrase)
                      )
                    ) {
                      issueMap["Customer Service"] += 1;
                    } else if (
                      contextRules["Guide Issues"].some((phrase) =>
                        text.includes(phrase)
                      )
                    ) {
                      issueMap["Guide Issues"] += 1;
                    }
                  } else {
                    issueMap[category] += 1;
                  }
                }
              });
            }
          );
        }
      });

      setFeedbackIssues(issueMap);
    };

    analyzeNegativeReviews();
  }, [reviews, complaintCategories, selectedPlatform, selectedProductCode]);

  useEffect(() => {
    if (Object.keys(feedbackIssues).length) {
      const ctx = document.getElementById(chartId).getContext("2d");

      if (chartRef.current) {
        chartRef.current.destroy();
      }

      chartRef.current = new ChartJS(ctx, {
        type: "bar",
        data: {
          labels: Object.keys(feedbackIssues),
          datasets: [
            {
              label: `Frequency of Issues (${selectedPlatform}, ${selectedProductCode})`,
              data: Object.values(feedbackIssues),
              backgroundColor: "rgba(75, 192, 192, 0.2)",
              borderColor: "rgba(75, 192, 192, 1)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          responsive: true,
          plugins: {
            title: {
              display: true,
              text: "Common Issues in Negative Reviews",
            },
            legend: { display: false },
          },
          scales: {
            y: {
              beginAtZero: true,
              grace: "10%",
              ticks: {
                stepSize: 1, // force increments of 1
                callback: function (value) {
                  if (Number.isInteger(value)) {
                    return value; // show only whole numbers
                  }
                  return null; // skip decimals
                },
              },
            },
          },
        },
      });
      chartRef.current.update();
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [feedbackIssues, chartId, selectedPlatform]);

  return (
    <div>
      <div className="filter-container">
        <div className="platform-filter">
          <label htmlFor="platform">Filter by Platform: </label>
          <select
            id="platform"
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="All">All</option>
            <option value="GYG">GYG</option>
            <option value="Viator">Viator</option>
            <option value="HEA">Headout</option>
            <option value="Civitatis">Civitatis</option>
            <option value="Tiquets">Tiqets</option>
          </select>
        </div>
        <div className="product-filter">
          <label htmlFor="productCode">Filter by Product Code: </label>
          <select
            id="productCode"
            value={selectedProductCode}
            onChange={(e) => setSelectedProductCode(e.target.value)}
          >
            <option value="All">All</option>
            {productCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>
      </div>
      <canvas id={chartId} className="analysisgraph-canvas"></canvas>
    </div>
  );
};

export default AnalysisGraph;
